/* Team with Roles (Page redesign: compact layout → Team Simplified.dc.html, 1a).

   A Role is a fleet's stored shortcut (CONTEXT.md: Role): what is stored and
   enforced is still each person's grants per account (permissionModel.Access).
   A person *has* a Role on an account only while linked to it there (editing
   the Role then updates them); matching grants alone make them Custom.
   This module reads a person's grants and links as "one Role + which accounts
   + Exceptions" and writes edits made in those terms back to per-account
   grants and links. Pure functions only. */

import type { CrmPermission, TeamRole, TeamRoleGrants } from '../../../api/client';
import {
  CRM_PERMISSIONS, MEDIA_DOWNLOAD, NO_ACCESS, crmKey, holdsCrm, isEmpty, sameAccess, setFans,
  type Access, type Flag, type Key,
} from './permissionModel';

/* ── The 33 permissions, as 1a groups them ─────────────────────────── */

/** `icon` + `hintKey` are set on items of a `split` area: each is its own button. */
export interface RoleItem { id: string; labelKey: string; keys: Key[]; icon?: string; hintKey?: string; /** Only grantable alongside a CRM permission (Media download). */ needsCrm?: boolean }
/** A product area: one toggle in the simple view, a checkbox group in Advanced.
    `master` is the area's own gate, which every other item needs — except the
    `free` ones, which only sit here for display. A `split` area (Tools) shows each
    item as its own button in the simple view: the items are unrelated rights. */
export interface RoleArea { id: string; labelKey: string; icon: string; hintKey: string; master: Flag | null; free?: Key[]; split?: boolean; items: RoleItem[] }

export const AREAS: RoleArea[] = [
  {
    id: 'crm', labelKey: 'team.crm', icon: 'messages-square', hintKey: 'ts.area.crm', master: null,
    items: CRM_PERMISSIONS.map((p) => ({ id: `crm-${p}`, labelKey: `fleet.crm.${p}`, keys: [crmKey(p)] })),
  },
  {
    // Media download (CONTEXT.md): its own switch, so turning the CRM on never
    // lets someone save media by accident. Needs a CRM permission.
    id: 'mediaDownload', labelKey: 'team.mediaDownload', icon: 'download', hintKey: 'ts.area.mediaDownload', master: null, split: true,
    items: [{ id: 'mediaDownload', labelKey: 'team.mediaDownload', keys: [MEDIA_DOWNLOAD], icon: 'download', hintKey: 'ts.area.mediaDownload', needsCrm: true }],
  },
  {
    id: 'chatAssistant', labelKey: 'team.chatAssistant', icon: 'sparkles', hintKey: 'ts.area.chatAssistant',
    master: 'access_chat_assistant', free: ['access_notifications_settings'],
    items: [
      { id: 'ca', labelKey: 'fleet.perm.basic', keys: ['access_chat_assistant'] },
      { id: 'caSetup', labelKey: 'team.assistantSetup', keys: ['access_assistant_setup'] },
      { id: 'caContent', labelKey: 'team.assistantContent', keys: ['access_assistant_content'] },
      { id: 'caModel', labelKey: 'team.modelInfo', keys: ['access_model_info'] },
      { id: 'caCensure', labelKey: 'team.censure', keys: ['access_censure'] },
      { id: 'caAudio', labelKey: 'team.audioSettings', keys: ['access_audio_settings'] },
      { id: 'caNotif', labelKey: 'team.notificationsSettings', keys: ['access_notifications_settings'] },
    ],
  },
  {
    // The Statistics page: revenue and stats of the account. Needs no AI access.
    id: 'stats', labelKey: 'team.stats', icon: 'bar-chart-2', hintKey: 'ts.area.stats', master: null,
    items: [{ id: 'stats', labelKey: 'team.stats', keys: ['access_stats'] }],
  },
  {
    id: 'mass', labelKey: 'team.massMessages', icon: 'send', hintKey: 'ts.area.mass', master: 'access_mass_messages',
    items: [
      { id: 'mm', labelKey: 'fleet.perm.basic', keys: ['access_mass_messages'] },
      { id: 'mmLists', labelKey: 'fleet.perm.lists', keys: ['access_mass_messages_lists'] },
      { id: 'mmSend', labelKey: 'fleet.perm.campaigns', keys: ['access_mass_messages_campaigns', 'access_mass_messages_schedules'] },
    ],
  },
  {
    id: 'priority', labelKey: 'team.priorityMassMessages', icon: 'zap', hintKey: 'ts.area.priority', master: 'access_priority_mass_messages',
    items: [
      { id: 'pm', labelKey: 'fleet.perm.basic', keys: ['access_priority_mass_messages'] },
      { id: 'pmLists', labelKey: 'fleet.perm.lists', keys: ['access_priority_mass_messages_lists'] },
      { id: 'pmSend', labelKey: 'fleet.perm.campaigns', keys: ['access_priority_mass_messages_campaigns', 'access_priority_mass_messages_schedules'] },
    ],
  },
  {
    id: 'automated', labelKey: 'team.automatedMessages', icon: 'bot', hintKey: 'ts.area.automated', master: 'access_automated_messages',
    items: [
      { id: 'am', labelKey: 'fleet.perm.basic', keys: ['access_automated_messages'] },
      { id: 'amSetup', labelKey: 'team.automatedMessagesSetup', keys: ['access_automated_messages_setup'] },
    ],
  },
  {
    // ADR 0031: the sidebar Tools, per Account like everything else.
    id: 'tools', labelKey: 'team.tools', icon: 'tool', hintKey: 'ts.area.tools', master: null, split: true,
    items: [
      { id: 'toolBulkMedia', labelKey: 'team.bulkMedia', keys: ['access_bulk_media'], icon: 'upload-cloud', hintKey: 'ts.tool.bulkMedia' },
      { id: 'toolAudio', labelKey: 'team.aiAudio', keys: ['access_audio'], icon: 'mic', hintKey: 'ts.tool.audio' },
      { id: 'toolDynamicLists', labelKey: 'team.dynamicLists', keys: ['access_dynamic_lists'], icon: 'list-filter', hintKey: 'ts.tool.dynamicLists' },
      { id: 'toolTrackingLinks', labelKey: 'team.trackingLinks', keys: ['access_tracking_links'], icon: 'link', hintKey: 'ts.tool.trackingLinks' },
    ],
  },
];

/** Account controls: off by default, each with why it is sensitive. */
export const SENSITIVE: (RoleItem & { whyKey: string })[] = [
  { id: 'acToken', labelKey: 'team.accountSettingsToken', keys: ['access_token'], whyKey: 'ts.sensitive.token' },
  { id: 'acPay', labelKey: 'team.pay', keys: ['access_pay'], whyKey: 'ts.sensitive.pay' },
  { id: 'acReconnect', labelKey: 'fleet.perm.reconnect', keys: ['access_reconnect'], whyKey: 'ts.sensitive.reconnect' },
  { id: 'acPermissions', labelKey: 'team.permissionManagement', keys: ['access_permission_management'], whyKey: 'ts.sensitive.permissions' },
];

const ALL_ITEMS: RoleItem[] = [...AREAS.flatMap((a) => a.items), ...SENSITIVE];
export const ITEM_COUNT = ALL_ITEMS.length;

export const itemOn = (a: Access, item: RoleItem) => item.keys.every((k) => a.has.has(k));
export const itemsOn = (a: Access) => ALL_ITEMS.filter((i) => itemOn(a, i)).length;
export const areaCount = (a: Access, area: RoleArea) => area.items.filter((i) => itemOn(a, i)).length;

/** The section rules: an item needs its area's gate; View only and subscriber
    entries exist only for what is granted (entries need Chats). */
export function normalise(a: Access): Access {
  const has = new Set(a.has);
  for (const area of AREAS) {
    if (!area.master) continue;
    const subs = area.items.flatMap((i) => i.keys).filter((k) => k !== area.master && !area.free?.includes(k));
    if (subs.some((k) => has.has(k))) has.add(area.master);
  }
  if (!holdsCrm(has)) has.delete(MEDIA_DOWNLOAD);
  const view = new Set([...a.view].filter((p) => has.has(crmKey(p))));
  return setFans({ has, view, fans: [] }, has.has(crmKey('chats')) ? a.fans : []);
}

export function toggleItem(a: Access, area: RoleArea | null, item: RoleItem): Access {
  const has = new Set(a.has);
  if (itemOn(a, item)) {
    item.keys.forEach((k) => has.delete(k));
    // Turning the gate off takes the area with it (display-only items stay).
    if (area?.master && item.keys.includes(area.master)) {
      area.items.forEach((i) => i.keys.forEach((k) => { if (!area.free?.includes(k)) has.delete(k); }));
    }
  } else {
    item.keys.forEach((k) => has.add(k));
  }
  return normalise({ ...a, has });
}

/** The simple view's switch: an area with anything on goes off, else all on. */
export function toggleArea(a: Access, area: RoleArea): Access {
  const keys = area.items.flatMap((i) => i.keys);
  const has = new Set(a.has);
  const anyOn = areaCount(a, area) > 0;
  keys.forEach((k) => (anyOn ? has.delete(k) : has.add(k)));
  return normalise({ ...a, has });
}

/* ── Roles ↔ grants ────────────────────────────────────────────────── */

export function accessOfRole(grants: TeamRoleGrants): Access {
  const has = new Set<Key>(grants.flags as Flag[]);
  const view = new Set<CrmPermission>();
  for (const [p, level] of Object.entries(grants.crm) as [CrmPermission, string][]) {
    has.add(crmKey(p));
    if (level === 'view') view.add(p);
  }
  if (grants.media_download) has.add(MEDIA_DOWNLOAD);
  return normalise({ has, view, fans: [] });
}

export function grantsOf(a: Access): TeamRoleGrants {
  const flags = [...a.has].filter((k): k is Flag => !k.startsWith('crm.'));
  const crm: TeamRoleGrants['crm'] = {};
  CRM_PERMISSIONS.forEach((p) => { if (a.has.has(crmKey(p))) crm[p] = a.view.has(p) ? 'view' : 'full'; });
  return { flags, crm, media_download: a.has.has(MEDIA_DOWNLOAD) };
}

/** Grants equal, ignoring Per-subscriber entries (they are never part of a Role). */
export const sameGrants = (x: Access, y: Access) => sameAccess({ ...x, fans: [] }, { ...y, fans: [] });
const signature = (a: Access) => `${[...a.has].sort().join(',')}|${[...a.view].sort().join(',')}`;

/** What a person "has": a Role id (linked), 'custom' (not linked) or 'none'. */
export type RoleRef = string;
export const NONE = 'none';
export const CUSTOM = 'custom';

/** A link counts while its Role is still in the list and the grants still
    equal it (the server keeps it that way; this guards a stale read). */
export function refOf(a: Access, link: string | null | undefined, roles: TeamRole[]): RoleRef {
  if (isEmpty(a)) return NONE;
  const role = link ? roles.find((r) => r.id === link) : undefined;
  return role && sameGrants(accessOfRole(role.grants), a) ? role.id : CUSTOM;
}

/** The Role whose grants equal these, if any: a Custom person's "same as". */
export function matchingRole(a: Access, roles: TeamRole[]): TeamRole | null {
  if (isEmpty(a)) return null;
  return roles.find((r) => sameGrants(accessOfRole(r.grants), a)) ?? null;
}

/** A RoleRef as the link to store: only a Role id is one. */
export const linkOfRef = (ref: RoleRef): string | null => (ref === NONE || ref === CUSTOM ? null : ref);

/* ── A person in one fleet: Role + Accounts + Exceptions ────────────── */

export interface Exception { accountId: string; access: Access; ref: RoleRef; /** Same grants as the main Role, only subscriber entries differ. */ follows: boolean }

export interface Plan {
  /** The grants the person has on most of their accounts (no subscriber entries). */
  main: Access;
  ref: RoleRef;
  scope: 'all' | 'some';
  /** Specific accounts on the main Role (not counting Exceptions). */
  ids: string[];
  exceptions: Exception[];
}

/** UI choices the grants alone can't express (kept per person, not stored):
    the scope switch while it changes nothing yet, and a Role picked while it
    covers no account. */
export interface PlanHint { scope?: 'all' | 'some'; main?: Access; ref?: RoleRef }

/** Read one person's grants and links on the fleet's accounts as a Plan: the
    biggest group of identical grants on the same Role (or Custom) is their
    Role; other accounts are Exceptions. */
export function derivePlan(
  accounts: string[], accessOf: (id: string) => Access, linkOf: (id: string) => string | null | undefined,
  roles: TeamRole[], hint: PlanHint = {},
): Plan {
  const ref = (id: string) => refOf(accessOf(id), linkOf(id), roles);
  const held = accounts.filter((id) => !isEmpty(accessOf(id)));
  const groups = new Map<string, string[]>();
  for (const id of held) {
    const key = `${signature(accessOf(id))}#${ref(id)}`;
    groups.set(key, [...(groups.get(key) ?? []), id]);
  }
  // Biggest group wins; on a tie, the one on a Role, then the first.
  const ranked = [...groups.values()].sort((x, y) => {
    if (y.length !== x.length) return y.length - x.length;
    const isRole = (ids: string[]) => Number(ref(ids[0]!) !== CUSTOM);
    return isRole(y) - isRole(x);
  });
  const mainIds = ranked[0] ?? [];
  const main = mainIds.length ? { ...accessOf(mainIds[0]!), fans: [] } : hint.main ?? NO_ACCESS;
  const mainRef = mainIds.length ? ref(mainIds[0]!) : hint.ref ?? (isEmpty(main) ? NONE : CUSTOM);
  const scope = hint.scope ?? (held.length > 0 && held.length === accounts.length ? 'all' : held.length ? 'some' : 'all');
  const inMain = new Set(mainIds);
  const exceptions: Exception[] = [];
  const ids: string[] = [];
  for (const id of accounts) {
    const a = accessOf(id);
    if (inMain.has(id) && !a.fans.length) { ids.push(id); continue; }
    if (inMain.has(id)) { exceptions.push({ accountId: id, access: a, ref: mainRef, follows: true }); continue; }
    if (!isEmpty(a)) { exceptions.push({ accountId: id, access: a, ref: ref(id), follows: false }); continue; }
    if (scope === 'all' && !isEmpty(main)) exceptions.push({ accountId: id, access: NO_ACCESS, ref: NONE, follows: false });
  }
  return { main, ref: mainRef, scope, ids, exceptions };
}

/** The per-account grants a Plan stands for. */
export function compilePlan(accounts: string[], plan: Plan): Map<string, Access> {
  const out = new Map<string, Access>();
  const ex = new Map(plan.exceptions.map((e) => [e.accountId, e]));
  const inScope = new Set(plan.ids);
  for (const id of accounts) {
    const e = ex.get(id);
    if (e) out.set(id, e.follows ? setFans({ ...plan.main, fans: [] }, e.access.fans) : e.access);
    else if (plan.scope === 'all' || inScope.has(id)) out.set(id, plan.main);
    else out.set(id, NO_ACCESS);
  }
  return out;
}

/** The links a Plan stands for: the Role each account follows, or null. */
export function compileLinks(accounts: string[], plan: Plan): Map<string, string | null> {
  const out = new Map<string, string | null>();
  const ex = new Map(plan.exceptions.map((e) => [e.accountId, e]));
  const inScope = new Set(plan.ids);
  for (const id of accounts) {
    const e = ex.get(id);
    if (e) out.set(id, linkOfRef(e.follows ? plan.ref : e.ref));
    else if (plan.scope === 'all' || inScope.has(id)) out.set(id, linkOfRef(plan.ref));
    else out.set(id, null);
  }
  return out;
}

/** Give the person a Role (`ref`: its id) or Custom grants (`ref` CUSTOM)
    wherever their main Role applies. Someone with no accounts yet gets it on
    every account. */
export function withMain(plan: Plan, main: Access, ref: RoleRef): Plan {
  const nextRef = isEmpty(main) ? NONE : ref;
  // An Exception that now equals the main Role is a plain account of it again.
  const merged = plan.exceptions.filter((e) => !e.follows && sameAccess(e.access, main) && e.ref === nextRef).map((e) => e.accountId);
  const exceptions = plan.exceptions.filter((e) => !merged.includes(e.accountId));
  return { ...plan, main, ref: nextRef, ids: [...plan.ids, ...merged], exceptions };
}

export function withScope(plan: Plan, scope: 'all' | 'some', accounts: string[]): Plan {
  if (scope === plan.scope) return plan;
  if (scope === 'all') {
    // Every account joins; "No access" exceptions stay only if they were explicit.
    return { ...plan, scope, ids: accounts.filter((id) => !plan.exceptions.some((e) => e.accountId === id)), exceptions: plan.exceptions.filter((e) => !isEmpty(e.access) || e.follows) };
  }
  // Specific keeps what they cover today.
  const covered = accounts.filter((id) => !plan.exceptions.some((e) => e.accountId === id));
  return { ...plan, scope, ids: covered, exceptions: plan.exceptions.filter((e) => !isEmpty(e.access) || e.follows) };
}

export function withAccounts(plan: Plan, ids: string[]): Plan {
  const ex = new Set(plan.exceptions.map((e) => e.accountId));
  return { ...plan, ids: [...new Set(ids)].filter((id) => !ex.has(id)) };
}

/** Add or change an Exception: on this account the person gets `access`, on
    Role `ref` (a Role id, CUSTOM, or NONE for no access). */
export function withException(plan: Plan, accountId: string, access: Access, ref: RoleRef): Plan {
  const rest = plan.exceptions.filter((e) => e.accountId !== accountId);
  const prev = plan.exceptions.find((e) => e.accountId === accountId);
  const fans = prev?.access.fans ?? [];
  const next = setFans(access, isEmpty(access) ? [] : fans);
  const nextRef = isEmpty(next) ? NONE : ref;
  // Same Role as the main one: kept as a row that follows it (subscriber
  // entries can be added there); it reads back as a plain account once saved.
  const follows = nextRef === plan.ref && sameGrants(next, plan.main);
  return { ...plan, ids: plan.ids.filter((id) => id !== accountId), exceptions: [...rest, { accountId, access: next, ref: nextRef, follows }] };
}

export function withoutException(plan: Plan, accountId: string): Plan {
  const exceptions = plan.exceptions.filter((e) => e.accountId !== accountId);
  return { ...plan, ids: [...new Set([...plan.ids, accountId])], exceptions };
}

/** Subscriber entries never change the Role an account follows. */
export function withExceptionFans(plan: Plan, accountId: string, fans: Access['fans']): Plan {
  const prev = plan.exceptions.find((e) => e.accountId === accountId);
  const own = prev && !prev.follows;
  const base = own ? prev.access : plan.main;
  const next = setFans({ ...base }, fans);
  if (!next.fans.length && !own) return withoutException(plan, accountId);
  const rest = plan.exceptions.filter((e) => e.accountId !== accountId);
  return { ...plan, ids: plan.ids.filter((id) => id !== accountId), exceptions: [...rest, { accountId, access: next, ref: own ? prev.ref : plan.ref, follows: !own }] };
}

/** A Role is used by a person when it is their main Role. */
export const usesRole = (plan: Plan, roleId: string) => plan.ref === roleId;
